import React from "react";
import { ScrollText, UserCheck, FileText, Shield, AlertTriangle, Check, BookOpen, BarChart, Mail, ShieldAlert } from "lucide-react";
import LegalShell from "./LegalShell";

export default function Terms() {
  return (
    <LegalShell title="Terms of Use (EULA)" subtitle="Last Updated: October 1, 2026">
      <div className="prose prose-invert max-w-none">
        <div className="space-y-8">
              {/* Introduction Section */}
              <div className="rounded-2xl border border-white/10 bg-zinc-900/60 p-8">
                <h2 className="flex items-center gap-2 text-2xl font-bold mb-6">
                  <ScrollText className="h-6 w-6 text-yellow-500" />
                  Introduction
                </h2>
                <p className="text-zinc-300">
                  Welcome to Pagent Social Corp. These Terms of Service (also referred to as our "Terms of Use" or 
                  "Terms &amp; Conditions") constitute a legally binding end user license agreement ("EULA") that governs 
                  your use of our website, applications, content, products and services ("Services"). By accessing or 
                  using our Services, you agree to be bound by these terms and all applicable laws and regulations.
                </p>
                <p className="text-zinc-300 mt-4">
                  If you do not agree with any part of these terms, you may not access or use our Services.
                </p>
                <p className="text-zinc-300 mt-4 font-semibold">
                  Pagent has no tolerance for objectionable content or abusive users. Content and users that violate these terms may be removed from the Services without notice.
                </p>
              </div>

              {/* Acceptance of Terms */}
              <div className="rounded-2xl border border-white/10 bg-zinc-900/60 p-8">
                <h2 className="flex items-center gap-2 text-2xl font-bold mb-6">
                  <Check className="h-6 w-6 text-yellow-500" />
                  1. Acceptance of Terms
                </h2>
                <p className="text-zinc-300">
                  By accessing and using Pagent's services, including our mobile application and website, you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our services.
                </p>
                <p className="text-zinc-300 mt-4">
                  We may update and change these Terms from time to time. If we make material changes, we will notify you via the Services or by other means to provide you with the opportunity to review the changes before they become effective. Your continued use of our Services after any changes indicates your acceptance of the updated Terms.
                </p>
              </div>

              {/* Eligibility Section */}
              <div className="rounded-2xl border border-white/10 bg-zinc-900/60 p-8">
                <h2 className="flex items-center gap-2 text-2xl font-bold mb-6">
                  <UserCheck className="h-6 w-6 text-yellow-500" />
                  2. Eligibility
                </h2>
                <p className="text-zinc-300">
                  You must be at least 13 years old to use our services. If you are under 18, you must have parental consent to use our platform. By using our services, you represent that you meet these eligibility requirements.
                </p>
                <p className="text-zinc-300 mt-4">
                  We reserve the right to terminate accounts of users who misrepresent their age or identity, or otherwise do not meet our eligibility requirements.
                </p>

                <h3 className="text-xl font-semibold mt-6 mb-3">Face verification and one account per person</h3>
                <p className="text-zinc-300">
                  Each person may hold only one Pagent account. To enforce this and to confirm that you meet our minimum age requirement, we may ask you to complete face verification by taking a selfie in the app. Depending on our current settings, verification may be required when you create your account, before you take part in live matches, or periodically thereafter. Existing accounts may be given a grace period during which the prompt can be dismissed; after that period, or for new accounts, you will not be able to use the affected features until you verify.
                </p>
                <p className="text-zinc-300 mt-4">
                  If verification indicates that you already hold another account, or that you do not meet our minimum age requirement, access to your account or to the affected features may be restricted. You may appeal a verification decision from within the app, and a member of our team will review it. Attempting to defeat face verification, for example by using another person's likeness, a photograph, or a mask, is a violation of these Terms. How face verification data is collected, used, and retained is described in our Privacy Policy.
                </p>
              </div>

              {/* User Accounts Section */}
              <div className="rounded-2xl border border-white/10 bg-zinc-900/60 p-8">
                <h2 className="flex items-center gap-2 text-2xl font-bold mb-6">
                  <FileText className="h-6 w-6 text-yellow-500" />
                  3. User Accounts
                </h2>
                <p className="text-zinc-300">
                  To participate in contests and access certain features, you must create an account. You are responsible for:
                </p>
                <ul className="list-disc pl-6 mt-4 space-y-2">
                  <li>Maintaining the confidentiality of your account credentials</li>
                  <li>All activities that occur under your account</li>
                  <li>Providing accurate and up-to-date information</li>
                  <li>Promptly updating your information if it changes</li>
                  <li>Ensuring your account is not shared with or accessed by others</li>
                  <li>Holding only one account; creating additional accounts to bypass a restriction, suspension, or verification decision is prohibited</li>
                </ul>
                <p className="text-zinc-300 mt-4">
                  Certain features, such as posting directly to your followers outside of a contest, are available only to users who have reached a specified rank. Rank requirements for these features may change from time to time.
                </p>
                <p className="text-zinc-300 mt-4">
                  We reserve the right to disable any user account if, in our opinion, you have violated any provision of these Terms.
                </p>
              </div>

              {/* Contest Rules Section */}
              <div className="rounded-2xl border border-white/10 bg-zinc-900/60 p-8">
                <h2 className="flex items-center gap-2 text-2xl font-bold mb-6">
                  <BarChart className="h-6 w-6 text-yellow-500" />
                  4. Contest Rules
                </h2>
                <p className="text-zinc-300">
                  When participating in contests (referred to as "pagents" in the app) on our platform:
                </p>
                <ul className="list-disc pl-6 mt-4 space-y-2">
                  <li>Follow all contest-specific rules and guidelines</li>
                  <li>Submit only original content that you have the rights to use</li>
                  <li>Respect other participants and maintain appropriate conduct</li>
                  <li>Accept judges' decisions as final</li>
                  <li>Understand that prizes are subject to availability and may change without notice</li>
                  <li>Recognize that all decisions regarding contest outcomes are at our sole discretion</li>
                </ul>
                <p className="text-zinc-300 mt-4">
                  Contests may have additional terms and conditions, which will be provided at the time of the contest. These additional terms are incorporated into these Terms of Service.
                </p>
              </div>

              {/* Content Guidelines Section */}
              <div className="rounded-2xl border border-white/10 bg-zinc-900/60 p-8">
                <h2 className="flex items-center gap-2 text-2xl font-bold mb-6">
                  <Shield className="h-6 w-6 text-yellow-500" />
                  5. Content Guidelines
                </h2>
                <p className="text-zinc-300">
                  Users must not post content that is:
                </p>
                <ul className="list-disc pl-6 mt-4 space-y-2">
                  <li>Illegal or promotes illegal activities</li>
                  <li>Harassing, abusive, or threatening</li>
                  <li>Infringing on intellectual property rights</li>
                  <li>Sexually explicit or inappropriate</li>
                  <li>Promoting discrimination or hatred</li>
                  <li>False, deceptive, or misleading</li>
                  <li>Violating others' privacy or confidentiality</li>
                  <li>Child sexual abuse or exploitation (CSAE), including the creation, possession, or distribution of child sexual abuse material (CSAM)</li>
                  <li>Grooming, solicitation, or any other conduct intended to sexualize or harm minors</li>
                </ul>
                <p className="text-zinc-300 mt-4">
                  We reserve the right to remove any content that violates these guidelines or is otherwise objectionable, without prior notice. Repeated violations may result in account termination.
                </p>
                <p className="text-zinc-300 mt-4">
                  Reports of objectionable content are reviewed within 24 hours. Content found to violate these terms is removed, and the users responsible may be ejected from the Services.
                </p>
                <p className="text-zinc-300 mt-4 font-semibold">
                  Note: Certain violating content (e.g., Child Sexual Abuse Material (CSAM)) will result in your immediate and permanent suspension from our Services.
                </p>
              </div>

              {/* Child Sexual Exploitation Section */}
              <div className="rounded-2xl border border-white/10 bg-zinc-900/60 p-8 border-red-500/20">
                <h2 className="flex items-center gap-2 text-2xl font-bold mb-6">
                  <ShieldAlert className="h-6 w-6 text-red-500" />
                  6. Child Sexual Exploitation
                </h2>
                <p className="text-zinc-300">
                  Child exploitation is strictly forbidden, and Pagent has zero tolerance towards any content that contains or promotes child sexual exploitation.
                </p>
                
                <h3 className="text-xl font-semibold mt-6 mb-3">What is a violation of this policy?</h3>
                <p className="text-zinc-300 mb-3">Content that:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Depicts, promotes, encourages, or supports a child engaging in sexually explicit or sexually suggestive acts, including but not limited to illustrated, computer-generated, or other realistic depictions of a child in a sexually explicit context</li>
                  <li>Contains sexualized commentaries about or directed at a known or unknown minor</li>
                  <li>Links to third-party sites that host child sexual exploitation material</li>
                  <li>Shares fantasies about or promotes engagement in child sexual exploitation</li>
                  <li>Expresses a desire to obtain materials that feature child sexual exploitation</li>
                  <li>Recruits, advertises, or expresses an interest in a commercial sex act involving a child, or in harboring and/or transporting a child for sexual purposes</li>
                  <li>Sends sexually explicit media to a child</li>
                  <li>Engages or attempts to engage a child in a sexually explicit conversation</li>
                  <li>Attempts to obtain sexually explicit media from a child or to engage a child in sexual activity through blackmail or other incentives</li>
                  <li>Identifies alleged victims of childhood sexual exploitation by name or image</li>
                  <li>Promotes or normalizes sexual attraction to minors as a form of identity or sexual orientation</li>
                </ul>
              </div>

              {/* Termination Section */}
              <div className="rounded-2xl border border-white/10 bg-zinc-900/60 p-8">
                <h2 className="flex items-center gap-2 text-2xl font-bold mb-6">
                  <AlertTriangle className="h-6 w-6 text-yellow-500" />
                  7. Termination
                </h2>
                <p className="text-zinc-300">
                  We reserve the right to terminate or suspend accounts that violate these terms or engage in inappropriate behavior on our platform. Users may also delete their accounts at any time.
                </p>
                <p className="text-zinc-300 mt-4">
                  Upon termination, your right to use the Services will immediately cease. All provisions of these Terms which by their nature should survive termination shall survive, including ownership provisions, warranty disclaimers, indemnity, and limitations of liability.
                </p>
              </div>

              {/* Changes to Terms Section */}
              <div className="rounded-2xl border border-white/10 bg-zinc-900/60 p-8">
                <h2 className="flex items-center gap-2 text-2xl font-bold mb-6">
                  <BookOpen className="h-6 w-6 text-yellow-500" />
                  8. Changes to Terms
                </h2>
                <p className="text-zinc-300">
                  We may modify these terms at any time. Continued use of our services after changes constitutes acceptance of the modified terms.
                </p>
                <p className="text-zinc-300 mt-4">
                  If you do not agree to the modified terms, you should discontinue your use of the Services. We will make reasonable efforts to notify you of any material changes to these Terms.
                </p>
              </div>

              {/* Intellectual Property */}
              <div className="rounded-2xl border border-white/10 bg-zinc-900/60 p-8">
                <h2 className="flex items-center gap-2 text-2xl font-bold mb-6">
                  <Shield className="h-6 w-6 text-yellow-500" />
                  9. Intellectual Property
                </h2>
                <p className="text-zinc-300">
                  All content, features, and functionality of our Services, including but not limited to text, graphics, logos, icons, images, audio clips, digital downloads, and software, are owned by Pagent Social Corp., its licensors, or other providers and are protected by copyright, trademark, and other intellectual property laws.
                </p>
                <p className="text-zinc-300 mt-4">
                  You may not reproduce, distribute, modify, create derivative works of, publicly display, publicly perform, republish, download, store, or transmit any of the material on our Services without our prior written consent.
                </p>
              </div>

              {/* Contact Section */}
              <div className="rounded-2xl border border-white/10 bg-zinc-900/60 p-8">
                <h2 className="flex items-center gap-2 text-2xl font-bold mb-6">
                  <Mail className="h-6 w-6 text-yellow-500" />
                  Contact Us
                </h2>
                <p className="text-zinc-300 mb-6">
                  If you have any questions about these Terms of Service, or if you see content or an account that you believe violates this policy, including the Content Guidelines, you can report it directly through the reporting tools in the Pagent application, or by emailing:
                </p>
                <div className="flex flex-col gap-4 sm:flex-row">
                  <a
                    href="mailto:support@pagent.tv"
                    className="inline-flex items-center gap-2 rounded-full border border-white/15 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-white/5"
                  >
                    <Mail className="h-4 w-4 text-yellow-400" />
                    support@pagent.tv
                  </a>
                </div>
              </div>

              <div className="rounded-lg bg-white/5 p-4 text-center">
                <p className="text-sm text-zinc-300">
                  Last updated: October 1, 2026
                </p>
              </div>
        </div>
      </div>
    </LegalShell>
  );
} 
